import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { Drawer } from "./Drawer";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { cn } from "../../lib/utils";

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
}

export interface FilterPanelProps {
  groups: FilterGroup[];
  /** Valor selecionado por grupo (um valor por grupo; null = "todos"). */
  value: Record<string, string | null | undefined>;
  onChange: (key: string, value: string | null) => void;
  onClear: () => void;
  /** Filtros que nao cabem no modelo de opcao unica (texto livre, intervalo
   * de data) — ex: Ação/Desde/Até do Log de auditoria. Achado do smoke
   * comparativo 2026-09-22 (fix/redesign-fidelidade-2): esses campos viviam
   * num Card SEPARADO, sempre visivel, ANTES do FilterPanel — no mobile isso
   * empurrava a lista inteira pra baixo com um bloco que nenhuma outra tela
   * tem, quebrando o padrao "um so botao Filtros" que todo o resto do app
   * segue. Agora entram aqui: mesmo lugar (popover no desktop, Drawer no
   * mobile) que os filtros categoricos, plano §filtros "mesma lista nos dois
   * tamanhos". */
  children?: ReactNode;
}

const MOBILE_QUERY = "(max-width: 60rem)";

/**
 * Fidelidade 3 (23/09/2026, BRIEF-FIDELIDADE-3.md, frente F1): o protótipo
 * NÃO tem coluna fixa de filtros — é um botão único "Filtros · <resumo> ▾"
 * que abre um painel suspenso no desktop (popover ancorado no próprio botão)
 * e uma gaveta no mobile (igual já era). Trocou o LUGAR do filtro, a API
 * pública do componente (groups/value/onChange/onClear/children) ficou
 * igual de propósito — Canal/Busca/Log/Notificações continuam chamando do
 * mesmo jeito.
 */
export function FilterPanel({ groups, value, onChange, onClear, children }: FilterPanelProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const wrapRef = useRef<HTMLDivElement>(null);
  const activeCount = useMemo(
    () => Object.values(value).filter((v) => v != null && v !== "").length,
    [value],
  );

  // Resumo do botao: "Todos" sem filtro ativo, ou os rotulos selecionados
  // (o componente e generico e nao sabe o genero da entidade — "Todos"
  // serve pra qualquer tela, o handoff so usa "Todos"/"Todas" como exemplo).
  const summary = useMemo(() => {
    const labels = groups
      .map((g) => g.options.find((o) => o.value === value[g.key])?.label)
      .filter((l): l is string => Boolean(l));
    return labels.length > 0 ? labels.join(" · ") : "Todos";
  }, [groups, value]);

  // Popover desktop fecha em clique fora ou Escape — igual ao NotificationBell.
  useEffect(() => {
    if (!open || isMobile) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, isMobile]);

  const body = (
    <>
      {groups.map((group) => (
        <div key={group.key} className="stack" style={{ gap: ".25rem" }}>
          <div className="filters__group-label">{group.label}</div>
          {group.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className="filters__option"
              aria-selected={value[group.key] === opt.value}
              onClick={() =>
                onChange(group.key, value[group.key] === opt.value ? null : opt.value)
              }
            >
              <span className="grow">{opt.label}</span>
              {typeof opt.count === "number" ? (
                <span className="chip chip--mono">{opt.count}</span>
              ) : null}
            </button>
          ))}
        </div>
      ))}
    </>
  );

  const clearDisabled = activeCount === 0 && !children;

  return (
    <div className="filters-btn" ref={wrapRef}>
      <button
        type="button"
        className={cn("filters-trigger-btn", activeCount > 0 && "filters-trigger-btn--active")}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup={isMobile ? "dialog" : "true"}
      >
        <SlidersHorizontal className="icon" aria-hidden style={{ display: isMobile ? "block" : "none" }} />
        <span className="grow" style={{ textAlign: "left" }}>Filtros</span>
        <span className="filters-trigger-btn__summary">{summary}</span>
        <ChevronDown className="icon" aria-hidden style={{ width: "1rem", height: "1rem" }} />
      </button>

      {/* Desktop — painel suspenso ancorado no botao (o mesmo corte de
          60rem do MOBILE_QUERY acima). */}
      {open && !isMobile ? (
        <div className="filters-popover" role="dialog" aria-label="Filtros">
          <div className="spread">
            <span className="row__title" style={{ fontSize: "var(--text-base)" }}>Filtros</span>
            <button type="button" className="btn btn--ghost" style={{ minHeight: "auto", padding: 0 }} onClick={onClear} disabled={clearDisabled}>
              Limpar
            </button>
          </div>
          {body}
          {children}
        </div>
      ) : null}

      {/* Mobile — gaveta, igual ja era. */}
      <Drawer
        open={open && isMobile}
        onOpenChange={setOpen}
        title="Filtros"
        footer={
          <>
            <button type="button" className="btn btn--secondary grow" onClick={onClear} disabled={clearDisabled}>
              Limpar
            </button>
            <button type="button" className="btn btn--primary grow" onClick={() => setOpen(false)}>
              Ver resultados
            </button>
          </>
        }
      >
        {body}
        {children}
      </Drawer>
    </div>
  );
}
